import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { MapPin, PhoneCall, Mail, Clock, Send, ShieldCheck, ChevronDown, CheckCircle2, MessageSquare, Briefcase, Zap, Heart, Star } from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    nama: '',
    perusahaan: '',
    email: '',
    telepon: '',
    pesan: ''
  });

  const handleWhatsApp = () => {
    if (!formData.nama || !formData.telepon) {
      alert("Harap isi Nama Lengkap dan Nomor Telepon.");
      return;
    }
    
    const text = `Halo Tim Satala Dermatech!\nSaya ingin berdiskusi lebih lanjut terkait kebutuhan bisnis saya.\n\n*INFORMASI KONTAK*\nNama: ${formData.nama}\nPerusahaan: ${formData.perusahaan || '-'}\nEmail: ${formData.email || '-'}\nTelepon: ${formData.telepon}\n\n*PESAN KEBUTUHAN*\n${formData.pesan || '-'}\n\nMohon untuk dapat segera menghubungi saya kembali. Terima kasih!`;
    const phone = "6285183147319";
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-16 pb-20 px-6 lg:px-12 bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC] overflow-hidden" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              <div className="w-full lg:w-5/12 z-10">
                <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Kontak Kami</h3>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                  Mari Diskusikan Kebutuhan Bisnis Anda
                </h1>
                <p className="text-lg text-[#7A695B] mb-8 leading-relaxed max-w-md font-light">
                  Tim kami siap membantu Anda menemukan solusi maklon kosmetik yang tepat untuk meningkatkan efisiensi dan pertumbuhan brand Anda.
                </p>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1 flex items-center gap-2">
                  Hubungi Kami <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="w-full lg:w-7/12 relative z-10">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white p-2">
                  <div className="absolute inset-0 bg-[#D4AF37]/10 mix-blend-overlay z-10 rounded-xl"></div>
                  <img src="/Gambar Web/Diskusikan.png" alt="Tim Satala" className="w-full h-[400px] object-cover rounded-xl" />

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Info Cards Row */}
        <section className="relative -mt-10 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Alamat Kantor", desc: "Jalan Aster kavling Paspampres,\nKota Batu, Kec. Ciomas,\nKabupaten Bogor, Jawa Barat", icon: <MapPin /> },
                { title: "WhatsApp / Telepon", desc: "+62 8518 3147 319\n", icon: <PhoneCall /> },
                { title: "Email & Website", desc: "sataladermatechessential@gmail.com\nsataladermatechessential.com\n", icon: <Mail /> },
                { title: "Jam Operasional", desc: "Senin – Jumat\n08.30 – 17.30 WIB\n", icon: <Clock /> }
              ].map((info, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-xl shadow-[#302621]/5 border border-[#E6D5B8]/40 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-4">
                    {info.icon}
                  </div>
                  <h4 className="font-bold text-[#302621] text-base mb-3">{info.title}</h4>
                  <p className="text-sm text-[#7A695B] leading-relaxed whitespace-pre-line">{info.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form & Benefits Section */}
        <section className="py-24 px-6 lg:px-12 bg-[#FFFDF9]" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              
              {/* Form Column */}
              <div className="w-full lg:w-7/12">
                <h2 className="text-3xl font-bold text-[#302621] mb-2">Kirim Pesan Kepada Kami</h2>
                <p className="text-[#7A695B] mb-8">Isi formulir di bawah ini dan tim kami akan segera menghubungi Anda.</p>
                
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-[#302621]">Nama Lengkap <span className="text-red-500">*</span></label>
                      <Input value={formData.nama} onChange={e => setFormData({...formData, nama: e.target.value})} placeholder="Masukkan nama lengkap Anda" className="bg-white border-[#E6D5B8] h-12 rounded-xl focus-visible:ring-[#D4AF37]" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-[#302621]">Nama Perusahaan <span className="text-red-500">*</span></label>
                      <Input value={formData.perusahaan} onChange={e => setFormData({...formData, perusahaan: e.target.value})} placeholder="Masukkan nama perusahaan" className="bg-white border-[#E6D5B8] h-12 rounded-xl focus-visible:ring-[#D4AF37]" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-[#302621]">Email <span className="text-red-500">*</span></label>
                      <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Masukkan email Anda" className="bg-white border-[#E6D5B8] h-12 rounded-xl focus-visible:ring-[#D4AF37]" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-[#302621]">Nomor Telepon <span className="text-red-500">*</span></label>
                      <Input type="tel" value={formData.telepon} onChange={e => setFormData({...formData, telepon: e.target.value})} placeholder="Masukkan nomor telepon" className="bg-white border-[#E6D5B8] h-12 rounded-xl focus-visible:ring-[#D4AF37]" />
                    </div>
                  </div>


                  <div className="space-y-2">
                    <label className="text-sm font-bold text-[#302621]">Pesan <span className="text-red-500">*</span></label>
                    <Textarea value={formData.pesan} onChange={e => setFormData({...formData, pesan: e.target.value})} placeholder="Ceritakan kebutuhan bisnis Anda secara singkat..." className="min-h-[120px] bg-white border-[#E6D5B8] rounded-xl focus-visible:ring-[#D4AF37] resize-none" />
                  </div>

                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="privacy" className="mt-1 w-4 h-4 rounded border-[#E6D5B8] text-[#D4AF37] focus:ring-[#D4AF37]" />
                    <label htmlFor="privacy" className="text-sm text-[#7A695B] leading-relaxed">
                      Saya menyetujui <a href="#" className="font-bold text-[#8C6208] hover:underline">Kebijakan Privasi</a> dan <a href="#" className="font-bold text-[#8C6208] hover:underline">Syarat & Ketentuan</a>
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-6 pt-2">
                    <Button type="button" onClick={handleWhatsApp} size="lg" className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 rounded-xl shadow-md border-0 flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" /> Hubungi via WhatsApp
                    </Button>
                    <div className="flex items-center gap-2 text-sm text-[#7A695B]">
                      <ShieldCheck className="w-4 h-4 text-green-600" />
                      <span>Data Anda aman bersama kami</span>
                    </div>
                  </div>
                </form>
              </div>

              {/* Benefits Column */}
              <div className="w-full lg:w-5/12 bg-[#FCFAF6] border border-[#E6D5B8]/50 rounded-3xl p-8 lg:p-10">
                <h3 className="text-2xl font-bold text-[#302621] mb-8">Mengapa Berkonsultasi Dengan Kami?</h3>
                
                <div className="space-y-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] text-base mb-1">Analisis Kebutuhan Bisnis</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Kami menganalisis proses bisnis Anda untuk memberikan solusi formulasi terbaik.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] text-base mb-1">Transparansi Formulasi</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Lihat langsung bagaimana proses maklon kami dapat menjamin keamanan produk Anda.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] text-base mb-1">Konsultasi Implementasi</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Tim ahli kami siap membantu perencanaan dari konsep hingga peluncuran.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                      <Heart className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] text-base mb-1">Penawaran Terbaik</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Dapatkan penawaran Harga Pokok Produksi (HPP) yang kompetitif sesuai kualitas.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] text-base mb-1">Dukungan Penuh</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Dukungan teknis profesional sebelum, selama, dan setelah masa produksi.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-2xl font-bold text-[#302621] mb-6">Lokasi Kantor Kami</h2>
            <div className="relative w-full h-[400px] rounded-3xl overflow-hidden border border-[#E6D5B8]/50 shadow-sm bg-gray-100">
              {/* Google Maps Embed */}
              <iframe 
                src="https://maps.google.com/maps?q=Satala%20Dermatech%20Essential,%20Jalan%20Aster%20kavling%20Paspampres,%20Kota%20Batu,%20Ciomas&t=&z=15&ie=UTF8&iwloc=&output=embed" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full grayscale-[20%] contrast-125 mix-blend-multiply"
                title="Google Maps Satala Dermatech"
              ></iframe>
              
              {/* Overlay Card - Positioned top-left to cover native Google Maps UI */}
              <div className="absolute top-2 left-2 bg-white p-5 rounded-sm shadow-md max-w-[calc(100%-1rem)] md:max-w-[320px] border border-gray-200 z-10 pointer-events-auto">
                <h4 className="font-semibold text-gray-900 text-base mb-1 truncate">Satala Dermatech Essential</h4>
                
                {/* Rating matching Google Maps style */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-sm font-medium text-gray-700">5.0</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star key={star} className="w-3.5 h-3.5 fill-[#F2B900] text-[#F2B900]" />
                    ))}
                  </div>
                  <span className="text-xs text-blue-600 hover:underline cursor-pointer ml-1">(25 ulasan)</span>
                </div>
                
                <p className="text-xs text-gray-600 leading-relaxed mb-4 line-clamp-2">
                  Jalan Aster kavling Paspampres<br />Kota Batu, Kec. Ciomas<br />Kabupaten Bogor, Jawa Barat
                </p>
                <a href="https://share.google/om60DsBQwmdotxTCP" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full py-2 px-4 rounded-md text-sm font-medium text-blue-600 border border-blue-600 hover:bg-blue-50 transition-colors">
                  <MapPin className="w-4 h-4 mr-2" /> Lihat rute yang lebih besar
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 lg:px-12 bg-[#FCFAF6] border-t border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-[#302621] mb-12 text-center lg:text-left">Pertanyaan yang Sering Diajukan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-8">
              {[
                {
                  q: "Apa saja syarat maklon untuk Perseorangan?",
                  a: "Syarat maklon untuk perseorangan sangat mudah, Anda hanya perlu menyiapkan KTP (Kartu Tanda Penduduk) dan NPWP Pribadi aktif."
                },
                {
                  q: "Apa saja syarat maklon untuk Badan Usaha?",
                  a: "Untuk Badan Usaha (PT/CV), syarat yang dibutuhkan meliputi KTP Direktur, NPWP Perusahaan, NIB (Nomor Induk Berusaha), dan dokumen legalitas perusahaan lainnya."
                },
                {
                  q: "Bagaimana tahapan proses maklon di Satala Dermatech?",
                  a: "Proses maklon kami melalui 8 tahapan terstruktur: 1) Konsultasi ide dan konsep, 2) Pembuatan Sample (pengujian formula), 3) Pendaftaran Legalitas (BPOM, Halal, HAKI), 4) Desain Packaging, 5) PO Produksi massal, 6) Pengujian Kualitas (QC), 7) Pelunasan Pembayaran, dan 8) Pengiriman Barang ke lokasi Anda."
                },
                {
                  q: "Apakah Satala memfasilitasi legalitas BPOM, Halal, dan HAKI?",
                  a: "Ya, kami menyediakan layanan One-Stop Solution yang mencakup pendaftaran legalitas BPOM, sertifikasi Halal, hingga pendaftaran Hak Kekayaan Intelektual (HAKI)."
                },
                {
                  q: "Berapa Minimum Order Quantity (MOQ) yang berlaku?",
                  a: "Kami mendukung pertumbuhan brand Anda dengan syarat kuantitas yang terjangkau. Minimum Order Quantity (MOQ) di Satala Dermatech adalah mulai dari 1.000 pcs per varian produk."
                },
                {
                  q: "Apakah Satala Dermatech juga membantu proses penjualan?",
                  a: "Tentu! Kami tidak hanya memproduksi, tetapi juga memberikan dukungan pasca-produksi. Kami dapat membantu Anda dengan konsultasi strategi pemasaran, penyediaan materi promosi (marketing kit), serta pendampingan agar produk Anda laris di pasaran."
                }
              ].map((faq, idx) => (
                <div 
                  key={idx} 
                  className={`bg-white border p-5 rounded-2xl flex flex-col cursor-pointer transition-all ${openFaq === idx ? 'border-[#D4AF37] shadow-sm' : 'border-[#E6D5B8]/40 hover:border-[#D4AF37]'}`}
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-[#4A3B32] pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180 text-[#D4AF37]' : 'text-gray-400'}`} />
                  </div>
                  {openFaq === idx && (
                    <div className="mt-4 pt-4 border-t border-[#E6D5B8]/30 animate-in fade-in slide-in-from-top-2">
                      <p className="text-sm text-[#7A695B] leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-[#EFE8D8] rounded-3xl overflow-hidden relative shadow-sm border border-[#E6D5B8]/50">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-100 mix-blend-multiply hidden md:block">
                <img src="/Gambar Web/Botol berdiri.png" alt="Products" className="w-full h-full object-cover mask-image-gradient-left" />
              </div>
              <div className="relative z-10 p-12 md:p-16 w-full md:w-[55%] lg:w-1/2">
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#302621] mb-4 leading-tight">Siap Memulai Transformasi Bisnis?</h2>
                <p className="text-[#7A695B] text-lg leading-relaxed mb-8">
                  Jadwalkan konsultasi gratis bersama tim kami dan temukan bagaimana Satala Dermatech dapat membantu brand Anda tumbuh lebih cepat.
                </p>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-4 text-base rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1 font-semibold flex items-center w-fit">
                  Jadwalkan Konsultasi <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
